import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { HtmlTooltip } from '../../index';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

const StyledImage = styled.img`
  width: ${({ size }) => `${size.width}px`};
  height: ${({ size }) => `${size.height}px`};
  display: block;
  border: 1px solid ${({ theme }) => theme.palette.darkGrey};
`;

const StyledNoImage = styled.div`
  width: 300px;
  height: 170px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.darkGrey};
  border: 1px solid ${({ theme }) => theme.palette.darkGrey};
  background-color: ${({ theme }) => theme.palette.grey};
`;

const NoImage = () => {
  return (
    <StyledNoImage>
      <ImageNotSupportedIcon />
    </StyledNoImage>
  );
};

const getImageSize = ({ url, windowHeight, callback }) => {
  const img = new Image();
  img.src = url;

  img.onload = () => {
    const aspectRatio = img.width / img.height;
    let maxHeightPercentage = aspectRatio <= 1 ? 0.43 : 0.65;
    const maxHeightLimit = 300;

    const calculatedMaxHeight = windowHeight * maxHeightPercentage;
    const maxHeight = Math.min(calculatedMaxHeight, maxHeightLimit);

    let width = Math.min(maxHeight, img.width);
    let height = width / aspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }

    callback({ width, height });
  };

  img.onerror = () => {
    callback(null);
  };
};

const ThumbnailTooltip = ({ children, url, alt, scale = 1, popperProps = {} }) => {
  const isValidLink = url?.startsWith('https://');
  const [imageSize, setImageSize] = useState(null);

  const calculateImageSize = useCallback(() => {
    const windowHeight = window.innerHeight;
    getImageSize({
      url,
      windowHeight,
      callback: (size) => {
        if (size === null) {
          setImageSize(null);
          return;
        }
        if (typeof scale === 'number') {
          setImageSize({
            width: size.width * scale,
            height: size.height * scale,
          });
        } else {
          setImageSize(size);
        }
      },
    });
  }, [url, scale]);

  useEffect(() => {
    calculateImageSize();
    window.addEventListener('resize', calculateImageSize);

    return () => {
      window.removeEventListener('resize', calculateImageSize);
    };
  }, [calculateImageSize]);

  if (!imageSize) return <>{children}</>;

  return (
    <HtmlTooltip
      popperProps={popperProps}
      renderHtml={
        isValidLink ? (
          <StyledImage src={url} alt={alt} size={imageSize} />
        ) : (
          <NoImage />
        )
      }
    >
      {children}
    </HtmlTooltip>
  );
};

ThumbnailTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  url: PropTypes.string.isRequired,
  alt: PropTypes.string,
  scale: PropTypes.number,
};

export default ThumbnailTooltip;
